import React, {useEffect, useRef, useState} from 'react';
import {Empty, Image, Spin, Typography} from 'antd';
import {DownOutlined, UpOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {PodExemptMetricsType, PodExemptPodSampleType} from '../../graphql/podExempt/types';
import {POD_EXEMPT_PREVIEW_ROOT_CLASS} from './podExemptConstants';

interface Props {
  metrics: PodExemptMetricsType | null;
  /** 画廊滚入视口后调用，返回样本图片；由父组件决定走 wms 还是 qc 查询 */
  loadSamples: () => Promise<PodExemptPodSampleType[]>;
}

/**
 * 历史投递 POD 画廊（与「不同司机 POD」指标卡是一体的）。
 *
 * 懒加载三层，目的是不浪费流量：
 * 1. 详情主查询不含任何图片 URL，只有样本 orderId；
 * 2. 本组件用 IntersectionObserver，**滚入视口才发起图片查询**（presign URL 仅 10 分钟有效，
 *    提前签发也会过期）；
 * 3. 折叠态每司机一张卡、只渲染该单第一张作封面（厚度用 CSS 纸边表达，不下载多余图片），
 *    展开才渲染全部，且 img 均为 loading="lazy"。
 */
const PodExemptPodGallery: React.FC<Props> = ({metrics, loadSamples}) => {
  const {t} = useTranslation();
  const [expanded, setExpanded] = useState(false);
  /** 受控预览：记录当前打开「大图」的那张卡（按 orderId），null 表示未打开 */
  const [previewOrderId, setPreviewOrderId] = useState<string | null>(null);
  const [samples, setSamples] = useState<PodExemptPodSampleType[] | null>(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  /** 防止 observer 多次触发导致重复请求 */
  const requestedRef = useRef(false);

  const sampleCount = metrics?.podSampleOrderIds?.length ?? 0;
  /** 样本 id 串作为「换单」信号：路由只换 param 不重挂载，需据此重置已加载的图片 */
  const sampleKey = (metrics?.podSampleOrderIds ?? []).join(',');
  const calculating = !metrics || metrics.calcStatus === 'PENDING';

  useEffect(() => {
    // 换单即重置：路由 /review/:reportId 只换 param 不重挂载，
    // 必须在挂 observer 前把 requestedRef 清掉 —— 早期实现拆成两个 effect 且 reset 在后，
    // 导致换单时 observer 因 requestedRef 仍为 true 直接 return，新单图片永不加载
    requestedRef.current = false;
    setSamples(null);
    setExpanded(false);
    setPreviewOrderId(null);

    // 统计中或无样本时不必观察视口，也不发请求
    if (calculating || sampleCount === 0) {
      return;
    }
    const node = containerRef.current;
    if (!node) {
      return;
    }
    // 卸载/换单后 promise 才 resolve 时不得再 setState，否则闭包与结果都白留一份
    let cancelled = false;
    const request = () => {
      if (requestedRef.current) {
        return;
      }
      requestedRef.current = true;
      setLoading(true);
      loadSamples()
        .then((rows) => {
          if (!cancelled) {
            setSamples(rows);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setLoading(false);
          }
        });
    };
    // 浏览器不支持时退化为立即加载（功能优先于流量优化）
    if (typeof IntersectionObserver === 'undefined') {
      request();
      return () => {
        cancelled = true;
      };
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        request();
      }
    });
    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [sampleKey, calculating, sampleCount, loadSamples]);

  // null=没有任何数据源（「暂无统计」，别等）/ PENDING=自算未完成（「统计中」，稍后再看）；都用静态文字，不转圈
  const statusText = !metrics
    ? t('podExempt.review.noMetrics')
    : metrics.calcStatus === 'PENDING'
      ? t('podExempt.review.calculating')
      : null;
  const subtitle = statusText ?? t('podExempt.review.podGallerySubtitle', {
        drivers: metrics?.distinctDriverCnt ?? 0,
        photos: metrics?.podPhotoCnt ?? 0,
      });

  return (
    <div ref={containerRef}>
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <span className="text-gray-400 text-sm min-w-0 truncate">{subtitle}</span>
        {!calculating && sampleCount > 0 && (
          <Typography.Link className="flex-none" onClick={() => setExpanded((v) => !v)}>
            {t(expanded ? 'podExempt.review.collapseAll' : 'podExempt.review.viewAll')}{' '}
            {expanded ? <UpOutlined /> : <DownOutlined />}
          </Typography.Link>
        )}
      </div>

      {calculating && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={statusText} />}

      {!calculating && sampleCount === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('podExempt.review.noPodSample')} />
      )}

      {!calculating && sampleCount > 0 && loading && <Spin size="small" />}

      {!calculating && samples && samples.length > 0 && !expanded && (
        // 折叠态：**一个司机一张卡、横向排列**（原型样式）。
        // 旧实现把所有司机用负 margin 叠成一簇，看不出「一组 = 一个司机」，审核员难以逐司机比对。
        // 卡内只渲染封面图（每司机第一张），多张时用纯 CSS 垫两层纸边 + 角标表达厚度 ——
        // 不额外下载图片，折叠态带宽仍是「每司机 1 张」。
        <div className="flex items-start gap-3 flex-wrap">
          {samples.map((sample) => {
            const shots = sample.imageUrls.length;
            return (
              <div
                key={sample.orderId}
                className="w-44 flex-none rounded-lg border border-gray-200 bg-white"
              >
                <div className="relative pt-2 px-2">
                  {/* 堆叠厚度：越往上越窄、越浅，模拟纸张叠压 */}
                  {shots > 2 && <div className="absolute left-6 right-6 top-0 h-1.5 rounded-t bg-gray-100" />}
                  {shots > 1 && <div className="absolute left-4 right-4 top-1 h-1.5 rounded-t bg-gray-200" />}
                  <div className="relative rounded-md overflow-hidden bg-gray-100">
                    <Image.PreviewGroup
                      items={sample.imageUrls}
                      preview={{
                        rootClassName: POD_EXEMPT_PREVIEW_ROOT_CLASS,
                        visible: previewOrderId === sample.orderId,
                        onVisibleChange: (v) => setPreviewOrderId(v ? sample.orderId : null),
                      }}
                    >
                      <Image
                        src={sample.imageUrls[0]}
                        width="100%"
                        height={128}
                        style={{objectFit: 'cover', display: 'block'}}
                        loading="lazy"
                        preview={{mask: null}}
                      />
                    </Image.PreviewGroup>
                    {shots > 1 && (
                      <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/55 text-white text-xs leading-none">
                        {shots}
                      </span>
                    )}
                  </div>
                </div>
                <div className="px-3 pt-2 pb-2.5">
                  <div className="text-sm truncate" title={sample.driverName ?? undefined}>
                    {sample.driverName ?? t('podExempt.review.unknownDriver')}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate">
                    {sample.deliveredAt ? new Date(sample.deliveredAt).toLocaleString() : '-'}
                  </div>
                  <div className="mt-1.5 text-right">
                    <Typography.Link className="text-xs" onClick={() => setPreviewOrderId(sample.orderId)}>
                      {t('podExempt.review.viewLarge')}
                    </Typography.Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!calculating && samples && samples.length > 0 && expanded && (
        // 展开态：一行一个司机，行内该单全部照片
        <Image.PreviewGroup preview={{rootClassName: POD_EXEMPT_PREVIEW_ROOT_CLASS}}>
          <div className="flex flex-col gap-3">
            {samples.map((sample) => (
              <div key={sample.orderId} className="flex gap-3 items-start">
                <div className="w-32 flex-none leading-normal">
                  <div className="text-sm break-words">
                    {sample.driverName ?? t('podExempt.review.unknownDriver')}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {sample.deliveredAt ? new Date(sample.deliveredAt).toLocaleString() : '-'}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 min-w-0">
                  {sample.imageUrls.map((url) => (
                    <Image
                      key={url}
                      src={url}
                      width={72}
                      height={72}
                      style={{objectFit: 'cover', borderRadius: 6}}
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      )}
    </div>
  );
};

export default PodExemptPodGallery;
